import { useParams, Link } from "react-router-dom";
import { getBuildingBySlug } from "../data/buildings";
import { useAvailability } from "../hooks/useAvailability";
import StudioCard from "../components/common/StudioCard";
import EnquiryForm from "../components/common/EnquiryForm";

export default function LocationDetail() {
  const { slug } = useParams();
  const building = getBuildingBySlug(slug);
  const { getStudiosByBuildingSlug, loading } = useAvailability();

  if (!building) {
    return (
      <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Location not found</h1>
          <Link to="/our-locations" className="text-teal-700 hover:underline text-base">Back to all locations</Link>
        </div>
      </main>
    );
  }

  const studios = getStudiosByBuildingSlug(slug);

  return (
    <main className="pt-16 md:pt-20">
      {/* Hero */}
      <section
        className="relative h-64 md:h-96 flex items-end"
        style={{ backgroundImage: `url('${building.heroImageUrl}')`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 pb-8">
          <h1 className="text-3xl md:text-5xl font-semibold text-white">{building.area} - {building.name}</h1>
          <p className="text-white/80 text-base mt-2">{building.address}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-8 md:px-16 lg:px-24 py-12 bg-[#f5f4f0]">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-2/3">
            <p className="text-gray-700 leading-relaxed text-base mb-6">{building.shortDescription}</p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { label: "Capacity", value: building.capacity },
                { label: "Size", value: building.sizeRange },
                { label: "Price", value: building.priceFrom },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white rounded p-4">
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">{label}</p>
                  <p className="text-base font-semibold text-gray-900">{value}</p>
                </div>
              ))}
            </div>

            <p className="text-gray-600 leading-relaxed text-base mb-4">{building.longDescription}</p>
            {building.extraDescription && <p className="text-gray-600 leading-relaxed text-base mb-4">{building.extraDescription}</p>}

            {building.galleryImageUrls.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                {building.galleryImageUrls.map((url, i) => (
                  <img key={i} src={url} alt={`${building.name} ${i + 1}`} className="w-full aspect-square object-cover rounded" />
                ))}
              </div>
            )}
          </div>

          <div className="lg:w-1/3">
            <div className="bg-white rounded p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Specification</h3>
              <ul className="flex flex-col gap-2">
                {building.specification.map((spec) => (
                  <li key={spec} className="flex items-start gap-2 text-base text-gray-600">
                    <span className="text-teal-600 mt-0.5">✓</span>
                    {spec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="w-full px-8 md:px-16 lg:px-24 py-12 bg-white">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Current availability</h2>
        {loading ? (
          <p className="text-base text-gray-500">Loading...</p>
        ) : studios.length === 0 ? (
          <p className="text-base text-gray-500">Sorry there are no units available at the moment.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {studios.map((studio) => (
              <StudioCard key={studio.id} studio={studio} />
            ))}
          </div>
        )}
      </section>

      {/* Transport */}
      <section className="w-full px-8 md:px-16 lg:px-24 py-12 bg-[#f5f4f0]">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8">Location</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {building.transport.underground.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/static/images/icon-closeby-underground.svg" alt="Underground" className="w-6 h-6" onError={(e) => { e.target.style.display = "none"; }} />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Underground</h3>
              </div>
              <ul className="flex flex-col gap-2">
                {building.transport.underground.map((item) => (
                  <li key={item.name} className="text-base text-gray-600"><span className="font-medium">{item.name}</span> — {item.detail}</li>
                ))}
              </ul>
            </div>
          )}
          {building.transport.mainline.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/static/images/icon-closeby-mainline.svg" alt="Mainline" className="w-6 h-6" onError={(e) => { e.target.style.display = "none"; }} />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Mainline</h3>
              </div>
              <ul className="flex flex-col gap-2">
                {building.transport.mainline.map((item) => (
                  <li key={item.name} className="text-base text-gray-600"><span className="font-medium">{item.name}</span> — {item.detail}</li>
                ))}
              </ul>
            </div>
          )}
          {building.transport.amenities.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/static/images/icon-closeby-amenities.svg" alt="Amenities" className="w-6 h-6" onError={(e) => { e.target.style.display = "none"; }} />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Amenities</h3>
              </div>
              <ul className="flex flex-col gap-2">
                {building.transport.amenities.map((item) => (
                  <li key={item.name} className="text-base text-gray-600"><span className="font-medium">{item.name}</span> — {item.detail}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <EnquiryForm
        pageUri={`https://artistic-spaces.co.uk/our-locations/${slug}`}
        pageName={`${building.name} | Artistic Spaces`}
        preselectedLocation={building.name}
      />
    </main>
  );
}